import React, { FC, ReactElement, useState } from 'react';
import { useQueries, UseQueryResult } from 'react-query';
import { Box, Unstable_Grid2 as Grid } from '@mui/material';
import { sortBy } from 'lodash';

import {
  FeedKey,
  GetFeedDocument,
  GetFeedQuery,
  GetFeedQueryVariables,
  useGetFeedQuery,
} from 'api';
import {
  useCurrentUser,
  useFeedDuplicatesDispatch,
  useFollowed,
  useNotify,
  useSharesItems,
} from 'hooks';
import { FEED_LIMIT_GRID, GRID_VIEW_TIMELINE_RENDER_LIMIT } from 'app-constants';
import { transport } from 'app-react-query/protected-transport';

import { Activity, RightSidebar } from '../components';
import GridTimeline from './GridTimeline';
import GridTimelineGroup from './GridTimelineGroup';

interface Props {
  emptyState: ReactElement;
  sort: boolean;
  header: ReactElement;
}

const GridTimelineLayout: FC<Props> = ({ header, emptyState, sort }) => {
  const notify = useNotify();
  const currentUser = useCurrentUser();
  const dispatch = useFeedDuplicatesDispatch();
  const { groups } = useFollowed();
  const [fetchGroups, setFetchGroups] = useState(false);

  const sortedGroups = sortBy(groups, 'follower_count').reverse();

  const timelineVariables = {
    sort,
    key: FeedKey.Timeline,
    id: currentUser.id,
    limit: FEED_LIMIT_GRID,
  };

  const groupsAttributes = sortedGroups.map(group => ({
    title: group.title,
    variables: {
      sort,
      key: FeedKey.Group,
      id: group.id,
      limit: FEED_LIMIT_GRID,
    },
  }));

  const { results: activityResults, isInitialLoad: isActivityLoading } = useSharesItems(
    { scheduled: false, data: { limit: 3 } },
    { onError: notify.queryError }
  );

  const timelineQueryResult = useGetFeedQuery(timelineVariables, {
    onError: notify.queryError,
    onSuccess: ({ getFeed }) => {
      const ids = getFeed.results
        .map(({ content_id }) => content_id)
        .slice(0, GRID_VIEW_TIMELINE_RENDER_LIMIT);
      dispatch({ ids });
      setFetchGroups(true);
    },
  });

  const groupsQueriesResult = useQueries(
    groupsAttributes.map(group => ({
      queryKey: ['GET_FEED', group.variables],
      queryFn: transport<GetFeedQuery, GetFeedQueryVariables>(GetFeedDocument, group.variables),
      enabled: fetchGroups,
      onError: notify.queryError,
    }))
  );

  const groupsOptions = groupsAttributes.map((group, index) => ({
    title: group.title,
    variables: group.variables,
    groupQueryResult: groupsQueriesResult[index] as UseQueryResult<GetFeedQuery, Error>,
  }));

  const canRenderSectionTwo = !!groupsOptions[0];
  const canRenderSectionThree = !!groupsOptions[3];
  const isGroupsDone = groupsOptions
    .map(group => group.groupQueryResult)
    .some(query => query.isSuccess);

  function renderActivity(index: number) {
    if (isActivityLoading) {
      return null;
    }

    return <Activity shareItem={activityResults[index]} />;
  }

  return (
    <Box marginBottom={25}>
      <Grid container direction="column" spacing={1}>
        <Grid xs>{header}</Grid>
        <Grid xs container spacing={3}>
          <Grid xs md={8}>
            <GridTimeline emptyState={emptyState} timelineQueryResult={timelineQueryResult} />
          </Grid>
          <Grid xs md={4}>
            <RightSidebar showInvite={false} variant="primary" />
          </Grid>
        </Grid>
      </Grid>
      {canRenderSectionTwo && renderActivity(0)}
      {isGroupsDone && <GridTimelineGroup groupOptions={groupsOptions[0]} />}
      {isGroupsDone && <GridTimelineGroup groupOptions={groupsOptions[1]} />}
      {isGroupsDone && <GridTimelineGroup groupOptions={groupsOptions[2]} />}
      {canRenderSectionThree && renderActivity(1)}
      {isGroupsDone &&
        groupsOptions
          .slice(3, groupsOptions.length)
          .map((group, index) => <GridTimelineGroup key={index} groupOptions={group} />)}
    </Box>
  );
};

export default GridTimelineLayout;
